import { z } from "zod";
import { citationStatusSchema } from "./knowledge.ts";

/**
 * Product help (openspec change `agent-self-knowledge-help`, spec `agent-self-knowledge`): a
 * corpus generated from the repository at build time (`packages/help`), embedded in the API and
 * searched in process by the agent's `product_help` tool. Its passages are cited `H#` and
 * rendered as an "ESI help" chip that opens the screen the page names.
 */

/** The model-facing name of the product help tool (its UI parts are `tool-product_help`). */
export const PRODUCT_HELP_TOOL_NAME = "product_help";

/** Most characters of a `product_help` query (design D4). */
export const MAX_HELP_QUERY_CHARS = 200;

/** Most passages one `product_help` call returns. */
export const MAX_HELP_RESULTS = 5;

/** Most characters of one help passage (one section of a page, design D3). */
export const MAX_HELP_PASSAGE_CHARS = 1200;

/**
 * Who a page is for (design D5): `user` pages are served to everyone in a tenant, `admin` pages
 * to tenant and entity admins only, `operator` pages never inside a tenant.
 */
export const helpAudienceSchema = z.enum(["user", "admin", "operator"]);
export type HelpAudience = z.infer<typeof helpAudienceSchema>;

/**
 * The screens a help page may name, as route names the client resolves (design D7): never a
 * URL. `projectSettings.*` and `projectHome.*` need the conversation's project; `backoffice.*`
 * and `backofficeSettings.*` need an admin role. The web app's router test checks that each
 * resolves to a route and that every settings group has one.
 */
export const HELP_SCREENS = [
  "home",
  "projects",
  "conversations",
  "tasks",
  "knowledge",
  "account",
  "projectHome.conversations",
  "projectHome.files",
  "projectHome.wiki",
  "projectHome.memory",
  "projectHome.tasks",
  "projectSettings.general",
  "projectSettings.members",
  "projectSettings.assistant",
  "projectSettings.knowledge",
  "projectSettings.web",
  "projectSettings.danger",
  "backoffice.users",
  "backoffice.entities",
  "backoffice.directory",
  "backoffice.residency",
  "backoffice.models",
  "backoffice.reports",
  "backoffice.projects",
  "backoffice.audit",
  "backoffice.plan",
  "backoffice.memory",
  "backoffice.branding",
  "backoffice.support",
  "backofficeSettings.general",
  "backofficeSettings.security",
  "backofficeSettings.clients",
  "backofficeSettings.models",
  "backofficeSettings.knowledge",
  "backofficeSettings.limits",
  "backofficeSettings.integrations",
] as const;
export const helpScreenSchema = z.enum(HELP_SCREENS);
export type HelpScreen = z.infer<typeof helpScreenSchema>;

/** Whether a screen lives in a project (it opens in the conversation's project). */
export const isProjectScreen = (screen: HelpScreen): boolean =>
  screen.startsWith("projectHome.") || screen.startsWith("projectSettings.");

/** Whether a screen is in the backoffice (it opens only for tenant and entity admins). */
export const isBackofficeScreen = (screen: HelpScreen): boolean => screen.startsWith("backoffice");

/** A page ID: lower-case words joined by hyphens (`spec-web-search`, `settings-web-search`). */
export const helpPageIdSchema = z
  .string()
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
  .max(80);

/**
 * A help page's front matter (`docs/help/generated/<id>.md`, and later the hand-written pages of
 * `docs/help/pages/<lang>/<id>.md`, design D9). `sources` are repository paths; a hand-written
 * page records a content hash per source so the drift gate flags it when one changes. In the
 * Markdown files the front matter is also an OKF concept's (`type`, `description`, `tags`,
 * `generated.by`, `sources[].resource`), since `docs/` is an OKF bundle.
 * `generated.build` is the build the corpus was generated for (`dev` in the repository); the
 * serving API stamps every result with its own build ID.
 */
export const helpPageFrontMatterSchema = z.object({
  id: helpPageIdSchema,
  title: z.string().min(1).max(120),
  audience: helpAudienceSchema,
  screen: helpScreenSchema.optional(),
  /** The setting area the page documents (`settings-*` pages). */
  setting: z.string().min(1).max(80).optional(),
  /** Hand-written pages only: the page's language. Generated pages are English. */
  lang: z.enum(["en", "fr"]).optional(),
  sources: z
    .array(
      z.union([
        z.string().min(1),
        // OKF provenance (`docs/` is an OKF bundle): a hand-written page hashes each source.
        z.object({
          resource: z.string().min(1),
          sha256: z
            .string()
            .regex(/^[0-9a-f]{64}$/)
            .optional(),
        }),
      ]),
    )
    .min(1),
  generated: z.object({ build: z.string().min(1).max(64) }).optional(),
});
export type HelpPageFrontMatter = z.infer<typeof helpPageFrontMatterSchema>;

/** One section of a page: what a search returns and an `H#` citation is checked against. */
export const helpSectionSchema = z.object({
  heading: z.string().max(200),
  text: z.string().min(1).max(MAX_HELP_PASSAGE_CHARS),
});
export type HelpSection = z.infer<typeof helpSectionSchema>;

/**
 * A page of `index.json`: its front matter, sections and extra search terms (`terms`: the French
 * titles and labels of what the page covers, so French screen names match, design D3).
 */
export const helpIndexPageSchema = helpPageFrontMatterSchema.extend({
  sections: z.array(helpSectionSchema).min(1),
  terms: z.array(z.string().min(1).max(MAX_HELP_PASSAGE_CHARS)).default([]),
});
export type HelpIndexPage = z.infer<typeof helpIndexPageSchema>;

/** `docs/help/generated/index.json`: the whole corpus, as the API embeds it. */
export const helpIndexSchema = z.object({
  version: z.literal(1),
  build: z.string().min(1).max(64),
  pages: z.array(helpIndexPageSchema),
});
export type HelpIndex = z.infer<typeof helpIndexSchema>;

/** A help citation's handle: `H1`, `H2`, … (per turn, numbered after earlier messages). */
export const helpHandleSchema = z.string().regex(/^H\d{1,9}$/);

/** Whether `handle` is a help citation handle (any case). */
export const isHelpHandle = (handle: string): boolean =>
  helpHandleSchema.safeParse(handle.toUpperCase()).success;

/**
 * A citation of a product help passage (spec `cited-answers` "Help citations"): rendered as an
 * "ESI help" chip with the page title, opening `screen` when the viewer may open it, otherwise
 * the passage. Product text: no document access check, the message's label is its only gate.
 */
export const helpCitationSchema = z.object({
  handle: helpHandleSchema,
  status: citationStatusSchema,
  pageId: helpPageIdSchema,
  title: z.string().min(1).max(120),
  /** A screen this client does not know (a newer build's) is dropped, not refused. */
  screen: helpScreenSchema.optional().catch(undefined),
  /** The build whose corpus the passage came from. */
  buildId: z.string().min(1).max(64),
});
export type HelpCitation = z.infer<typeof helpCitationSchema>;

/**
 * The preview of one help citation (`GET …/messages/:messageId/citations/:handle`, `H#`): the
 * passage as cited, stored with the message. `versionChanged`: a newer build serves this page
 * with different text, or no longer has it (`pageGone`): "describes an earlier version of ESI".
 */
export const helpCitationPassageSchema = z.object({
  kind: z.literal("help"),
  pageId: helpPageIdSchema,
  title: helpCitationSchema.shape.title,
  screen: helpCitationSchema.shape.screen,
  buildId: helpCitationSchema.shape.buildId,
  status: citationStatusSchema,
  excerpt: z.string().max(MAX_HELP_PASSAGE_CHARS + 1),
  versionChanged: z.boolean(),
  pageGone: z.boolean(),
});
export type HelpCitationPassage = z.infer<typeof helpCitationPassageSchema>;
